import { useState } from "react";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  MenuItem,
} from "@mui/material";
import { useDeleteMessage } from "../hooks/useDeleteMessage";

export function MessageDeleteAction({
  conversationId,
  communityId,
  messageId,
  variant = "icon",
  onDialogOpen,
}: {
  conversationId: string;
  communityId?: string;
  messageId: string;
  variant?: "icon" | "menuitem";
  onDialogOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const deletion = useDeleteMessage(conversationId, communityId);

  const close = () => {
    if (deletion.isPending) return;
    setOpen(false);
    deletion.reset();
  };

  const openDialog = () => {
    deletion.reset();
    onDialogOpen?.();
    setOpen(true);
  };

  return (
    <>
      {variant === "menuitem" ? (
        <MenuItem onClick={openDialog}>
          <ListItemIcon>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </ListItemIcon>
          Delete message
        </MenuItem>
      ) : (
        <IconButton
          type="button"
          size="small"
          aria-label="Delete message"
          onClick={openDialog}
          sx={{ color: "inherit", opacity: 0.72 }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      )}
      <Dialog
        open={open}
        onClose={close}
        aria-labelledby="delete-message-title"
      >
        <DialogTitle id="delete-message-title">
          Delete this message?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Everyone in the conversation will see a deleted-message marker in
            its place.
          </DialogContentText>
          {deletion.isError ? (
            <Alert severity="error" role="alert" sx={{ mt: 2 }}>
              We couldn&apos;t delete this message. Please try again.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={close} disabled={deletion.isPending}>
            Cancel
          </Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            disabled={deletion.isPending}
            onClick={() =>
              deletion.mutate(messageId, {
                onSuccess: () => setOpen(false),
              })
            }
          >
            {deletion.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
